import React, { useState, useEffect, useCallback } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { makeStyles } from '@mui/styles';
import useActivityLogs from '@dyl/hooks/useActivityLogs';
import quickDateFormat from '@dyl/utils/quickDateFormat';

const useStyles = makeStyles(() => ({
  tableRow: {
    borderBottom: '1px solid #e0e0e0',
    marginBottom: 40,
  },
  tableContainer: {
    width: '100%',
    border: 'none',
  },
  accorionDetails: {
    padding: 0,
    margin: 0,
    zIndex: -1,
  },
  accordionSummary: {
    backgroundColor: '#f7f7f7',
    flexDirection: 'row-reverse', // Aligns icon to the left
    marginTop: '72px'
  },
  tableCell:{
    padding: '4px 8px',
    height: '50px'
  },
  tableCellNoEllipsis:{
    padding: '4px 8px',
  },
  accordion:{
    zIndex: 0
  }

}));

const columns = [{ name: 'Date', width: '15%' }, { name: 'Field or thing changed', width: '17%' }, { name: 'Changed from', width: '24%' }, { name: 'Changed to', width: '24%' }, { name: 'Changed by', width: '20%' }];
const rowsPerPage = 25; // Number of records per page


// custom function to handle array data
function formatCellValue(value) {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (typeof value === 'string') {
    try {
      const arr = JSON.parse(value);
      if (Array.isArray(arr)) {
        return arr.join(', ');
      }
    } catch {
      // Not a JSON array, return as is
    }
  }
  return value;
}

function ActivityLogs({ logType = 'test', logId, reload }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const [displayedRecords, setDisplayedRecords] = useState([]);

  const { records } = useActivityLogs(logType, logId, reload);
  const classes = useStyles();

  const handleChange = (event, isExpanded) => {
    setIsExpanded(isExpanded);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const renderLogs = useCallback(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return records.slice(startIndex, endIndex).map((row, rowIndex) => (
      <TableRow key={rowIndex}>
        <TableCell className={classes.tableCell}>{quickDateFormat(row.created_at)}</TableCell>
        <TableCell className={classes.tableCell}>{row.changed_field}</TableCell>
        <TableCell
          className={classes.tableCellNoEllipsis}
          style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
        >
          {formatCellValue(row.previous_value)}
        </TableCell>
        <TableCell
          className={classes.tableCellNoEllipsis}
          style={{ wordBreak: 'break-word', whiteSpace: 'pre-line' }}
        >
          {formatCellValue(row.current_value)}
        </TableCell>
        <TableCell className={classes.tableCell}>{row.user_name}</TableCell>
      </TableRow>
    ));
  }, [records, page]);

  useEffect(() => {
    setDisplayedRecords(renderLogs());
  }, [renderLogs]);

  return (
    <div>
      <Accordion className={classes.accordion} expanded={isExpanded} onChange={handleChange}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          className={classes.accordionSummary}
        >
          <Typography>{isExpanded ? 'Hide Activity Log' : 'Show Activity Log'}</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accorionDetails}>
          {records.length === 0 ? (
            <Typography sx={{ textAlign: 'center' }}>No Logs</Typography>
          ) : (
            <>
              <Grid container>
                <Grid item className={classes.tableContainer}>
                  <TableContainer component={Paper}>
                    <Table style ={{ tableLayout : 'fixed', width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          {columns.map((column, index) => (
                            <TableCell  key={index} style={{ maxWidth: column.width, margin: 0, padding: '8px' }} className={classes.tableCell}>{column.name}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>{displayedRecords}</TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
              {records.length > rowsPerPage && <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                <Pagination
                  count={Math.ceil(records.length / rowsPerPage)}
                  page={page}
                  onChange={handleChangePage}
                />
              </div>}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

export default ActivityLogs;
